import { useTranslation } from 'react-i18next';
import { activeModules, MODULES } from '@kraftwerk/shared';
import type { Module } from '@kraftwerk/shared';
import { db } from '../db/local.js';
import { updateModules } from '../db/profile.js';
import { useAvailableModules, useStoredModules } from '../hooks/useModules.js';

/**
 * Was ich nutze (E15). Je Modul ein Umschalter mit einem Satz, wofür es da
 * ist. Abschalten versteckt Reiter und Kacheln und stellt Erinnerungen ab —
 * die Daten bleiben, wieder einschalten bringt alles zurück. Das steht dabei,
 * damit niemand aus Vorsicht etwas anlässt, das er nicht braucht.
 */
export function ModulesSetting({ framed = true }: { framed?: boolean }): React.JSX.Element | null {
  const { t } = useTranslation();
  const stored = useStoredModules();
  // Kinder (E16) sehen nur, was sie einschalten können — als Teen mit
  // Freigabe der Eltern auch Ernährung (27.10.2026).
  const available = useAvailableModules();
  if (stored === undefined) return null;
  const on = activeModules(stored);

  const toggle = (module: Module) => {
    const next = on.includes(module) ? on.filter((entry) => entry !== module) : [...on, module];
    void updateModules(
      MODULES.filter((entry) => next.includes(entry)),
      db,
    );
  };

  return (
    <section
      aria-labelledby="settings-modules"
      className={framed ? 'kw-tile flex flex-col gap-2' : 'flex flex-col gap-2'}
    >
      <h4 id="settings-modules" className={framed ? 'kw-kicker' : 'sr-only'}>
        {t('modules.heading')}
      </h4>
      <p className="text-sm text-ink-muted">{t('modules.intro')}</p>
      <ul className="flex flex-col gap-2">
        {available.map((module) => (
          <li key={module}>
            <button
              type="button"
              aria-pressed={on.includes(module)}
              onClick={() => toggle(module)}
              className={`flex min-h-14 w-full items-center justify-between gap-3 rounded-xl border px-3 py-2 text-left ${
                on.includes(module) ? 'border-accent' : 'border-line'
              }`}
            >
              <span className="min-w-0">
                <span className="block text-sm font-medium">{t(`modules.name.${module}`)}</span>
                <span className="block text-xs text-ink-muted">{t(`modules.why.${module}`)}</span>
              </span>
              <span
                aria-hidden="true"
                className={`shrink-0 text-xs font-semibold ${
                  on.includes(module) ? 'text-accent' : 'text-ink-muted'
                }`}
              >
                {on.includes(module) ? t('modules.on') : t('modules.off')}
              </span>
            </button>
          </li>
        ))}
      </ul>
      <p className="text-xs text-ink-muted">{t('modules.keep')}</p>
    </section>
  );
}
